import React, { useState, useEffect, FormEvent, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, X, ChevronRight, CheckCircle, FileText, Calendar, Users, DollarSign, CreditCard, RefreshCcw, Package2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { sendProposalLink, setProposalServiceSelection, ProposalService, logProposalEvent, logProposalView } from '../lib/proposals';
import { createProposalViewedMilestone, createProposalApprovedMilestone } from '../lib/bookingMilestones';
import { useSupabase } from '../contexts/SupabaseContext';
import { useRouter } from 'next/router';
import { formatCurrency } from '../lib/utils';
import RichTextDescription from '../components/RichTextDescription';
import { ProductPhotoService } from '../services/productPhotoService';

interface ProposalToken {
  proposal_id: string;
  business_id: string;
  token: string;
  expires_at: string;
  created_at: string;
}

interface Business {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  logo_url?: string;
}

interface Customer {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

interface Service {
  id?: string;
  name: string;
  description: string;
  price: number; // This is the original price
  quantity: number;
  title: string;
  details?: string[];
  benefits?: string[];
  estimated_duration?: string;
  isSelectable?: boolean;
  is_selected?: boolean;
  isSelected?: boolean;
  discount_amount?: number;
  discount_type?: 'percentage' | 'dollar';
  discount_value?: number;
  offer?: {
    id: string;
    title: string;
    status: string;
  };
}

interface Product {
  id?: string;
  name: string;
  price: number;
  quantity: number;
  category_name?: string;
  stock_item_id?: string;
  discount_amount?: number;
  discount_type?: 'percentage' | 'dollar';
  discount_value?: number;
  offer?: {
    id: string;
    title: string;
    status: string;
  };
}

interface Proposal {
  id: string;
  business_id: string;
  customer_id: string;
  title: string;
  description: string;
  total_amount: number;
  status: 'draft' | 'sent' | 'viewed' | 'approved' | 'declined';
  valid_until: string;
  created_at: string;
  updated_at: string;
  services: Service[];
  products?: Product[];
  customer: Customer;
  notes?: string;
}

interface OfferProps {
  title: string;
  services: Service[];
  products?: Product[];
  productPhotos?: Record<string, string>;
  isExpanded: boolean;
  isSelected: boolean;
  onToggle: () => void;
  onSelect: () => void;
  tax?: number;
  promo?: number;
  isSelecting?: boolean;
}

const Offer: React.FC<OfferProps> = ({
  title,
  services,
  products = [],
  productPhotos = {},
  isExpanded,
  isSelected,
  onToggle,
  onSelect,
  tax = 0.05,
  promo = 0,
  isSelecting = false
}) => {
  // No local state for optional service selection - use database state directly
  // Add state for tracking service selection locally
  const [localServiceToggle, setLocalServiceToggle] = useState<Record<string, boolean>>({});

  // Component for product icon - shows photo if available, otherwise green Package2 icon
  const ProductIcon = ({ product }: { product: Product }) => {
    const photoUrl = productPhotos[product.stock_item_id || ''];
    
    if (photoUrl) {
      return (
        <div className="h-16 w-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
          <img 
            src={photoUrl} 
            alt={product.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, show fallback icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = '<div class="h-8 w-8 text-green-600"><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29,7 12,12 20.71,7"/><line x1="12" x2="12" y1="22" y2="12"/></svg></div>';
                parent.className = "h-16 w-16 rounded-lg bg-green-100 flex items-center justify-center";
              }
            }}
          />
        </div>
      );
    }
    
    return (
      <div className="h-16 w-16 rounded-lg bg-green-100 flex items-center justify-center">
        <Package2 className="h-8 w-8 text-green-600" />
      </div>
    );
  };
  
  // Calculate totals based on database selection state AND local toggle state
  const serviceSubtotal = services.reduce((total, service) => {
    // Only include the service in the total if:
    // 1. It's not optional (isSelectable === false), OR
    // 2. It's optional AND selected in the database (is_selected === true) or local state
    const isOptionalByName = service.name?.includes('(Optional)') || false;
    let isSelectable = Boolean(service.isSelectable);
    
    // If the service name indicates it's optional but isSelectable isn't set, force it to true
    if (isOptionalByName && !isSelectable) {
      isSelectable = true;
    }
    
    // Check both database state and local toggle state
    const serviceId = service.id || `service-${services.indexOf(service)}`;
    const isSelected = service.is_selected === true || localServiceToggle[serviceId] === true;
    const includeInTotal = !isSelectable || (isSelectable && isSelected);
    
    if (!includeInTotal) {
      return total;
    }
    
    const price = typeof service.price === 'number' ? service.price : 0;
    const quantity = typeof service.quantity === 'number' ? service.quantity : 1;
    return total + (price * quantity);
  }, 0);

  // Calculate product subtotal
  const productSubtotal = products.reduce((total, product) => {
    const price = typeof product.price === 'number' ? product.price : 0;
    const quantity = typeof product.quantity === 'number' ? product.quantity : 1;
    return total + (price * quantity);
  }, 0);

  const subtotal = serviceSubtotal + productSubtotal;

  // Calculate total discount amount from individual services
  const serviceDiscountAmount = services.reduce((totalDiscount, service) => {
    const isOptionalByName = service.name?.includes('(Optional)') || false;
    let isSelectable = Boolean(service.isSelectable);
    
    if (isOptionalByName && !isSelectable) {
      isSelectable = true;
    }
    
    const serviceId = service.id || `service-${services.indexOf(service)}`;
    const isSelected = service.is_selected === true || localServiceToggle[serviceId] === true;
    const includeInTotal = !isSelectable || (isSelectable && isSelected);
    
    if (!includeInTotal) {
      return totalDiscount;
    }
    
    const discountAmount = typeof (service as any).discount_amount === 'number' ? (service as any).discount_amount : 0;
    return totalDiscount + discountAmount;
  }, 0);

  // Calculate total discount amount from products
  const productDiscountAmount = products.reduce((totalDiscount, product) => {
    const discountAmount = typeof product.discount_amount === 'number' ? product.discount_amount : 0;
    return totalDiscount + discountAmount;
  }, 0);

  const totalDiscountAmount = serviceDiscountAmount + productDiscountAmount;
  
  // Calculate tax on discounted amount
  const discountedSubtotal = subtotal - totalDiscountAmount;
  const taxAmount = Math.round((discountedSubtotal * tax) * 100) / 100;
  const total = Math.round((discountedSubtotal - promo + taxAmount) * 100) / 100;

  // Format currency with fallback
  const formatCurrency = (value: number) => {
    if (isNaN(value)) return '$0.00';
    return `$${value.toFixed(2)}`;
  };

  return (
    <div 
      className={`
        relative overflow-hidden
        bg-white
        border border-gray-300
        rounded-xl sm:rounded-2xl
        transition-all duration-300
        group
        ${isExpanded ? 'shadow-xl shadow-gray-200/50' : 'hover:shadow-lg hover:shadow-gray-200/50'}
        ${isSelected ? 'ring-2 ring-[#00CC4D]/50 ring-offset-2 ring-offset-black/50' : ''}
        text-[13px] sm:text-base
      `}
    >
      <div 
        className={`
          cursor-pointer transition-all duration-300
          ${isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50/50'}
        `}
        onClick={onToggle}
      >
        <div className="flex items-center justify-between p-3 sm:p-5 gap-3 sm:gap-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="text-gray-700 bg-gray-100 rounded-full p-1.5 sm:p-2 shadow-inner border border-gray-200">
              {isExpanded ? <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5" /> : <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5" />}
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 font-orbitron">{title}</h3>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-6">
            <div className="text-right">
              <p className="text-lg sm:text-2xl font-bold text-[#00CC4D] font-orbitron">{formatCurrency(total)}</p>
              <p className="text-xs sm:text-sm text-gray-600">inc. GST</p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect();
              }}
              disabled={isSelecting}
              className={`
                relative px-4 sm:px-7 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium
                transition-all duration-300 font-orbitron
                before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-r
                before:transition-all before:duration-300 before:opacity-90
                ${isSelected 
                  ? 'before:from-[#00CC4D] before:to-[#00B344] text-white shadow-lg shadow-[#00CC4D]/30 scale-95 ring-1 sm:ring-2 ring-[#00CC4D]/50 ring-offset-1 sm:ring-offset-2 ring-offset-black/50'
                  : isSelecting
                    ? 'before:from-gray-500 before:to-gray-600 text-white cursor-not-allowed opacity-75'
                  : 'before:from-[#00CC4D] before:to-[#00B344] text-white hover:scale-105 hover:shadow-lg hover:shadow-[#00CC4D]/30'
                }
                overflow-hidden group whitespace-nowrap
                disabled:hover:scale-100 disabled:hover:shadow-none
              `}
            >
              <span className="relative inline-flex items-center">
                {isSelecting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    SELECTING...
                  </>
                ) : isSelected ? 'ACCEPTED' : 'ACCEPT'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-3 sm:px-6 pb-4 sm:pb-5 border-t border-gray-300 bg-gray-50/30">
          <div className="pt-1 sm:pt-2">
            <div className="space-y-2 sm:space-y-3">

              {/* Display non-selectable (main) services first */}
              {services
                .filter(service => !service.isSelectable && !service.name?.includes('(Optional)'))
                .map((service, index) => {
                  const serviceId = service.id || `service-${services.indexOf(service)}`;
                  return (
                    <div key={`main-${serviceId}`} className="pt-3 sm:pt-4 pb-3 sm:pb-4 bg-white border-2 border-green-400 rounded-lg shadow-sm">
                      <div className="px-3 sm:px-4">
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div className="flex-1">
                            <h4 className="font-semibold text-[14px] sm:text-[16px] font-orbitron text-gray-900">
                            {service.name.replace(/ \(Optional\)/gi, '')}
                          </h4>
                        </div>
                        <div className="flex items-center">
                          {/* Show discount pricing if service has discount */}
                          {(service as any).discount_amount && (service as any).discount_amount > 0 ? (
                            <div className="text-right mr-3">
                              <div className="text-sm text-gray-500 line-through">
                                {formatCurrency(typeof service.price === 'number' ? service.price : 0)}
                              </div>
                              <div className="text-lg font-bold text-[#00CC4D]">
                                {formatCurrency((typeof service.price === 'number' ? service.price : 0) - ((service as any).discount_amount || 0))}
                              </div>
                            </div>
                          ) : (
                            <div className="text-lg font-bold mr-3 text-[#00CC4D]">
                              {formatCurrency(typeof service.price === 'number' ? service.price : 0)}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        {service.description && (
                          <div className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed col-span-full mb-3 sm:mb-4">
                            <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 bg-[#00CC4D]" />
                              <div className="text-gray-700">
                              <RichTextDescription 
                                description={service.description} 
                                  className="prose-sm text-gray-700 prose-headings:text-gray-800 prose-strong:text-gray-800 prose-em:text-gray-600" 
                              />
                            </div>
                          </div>
                        )}
                        {service.details && service.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed">
                            <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 bg-[#00CC4D]" />
                              <p className="text-gray-700">{detail}</p>
                          </div>
                        ))}
                      </div>

                      {service.benefits && service.benefits.length > 0 && (
                        <div className="mt-4 sm:mt-5">
                            <h5 className="font-semibold mb-2 sm:mb-3 text-[13px] sm:text-[15px] font-orbitron text-gray-900">Key Benefits</h5>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                            {service.benefits.map((benefit, benefitIndex) => (
                              <div key={benefitIndex} className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed">
                                <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 bg-[#00CC4D]" />
                                  <p className="text-gray-700">{benefit}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {service.estimated_duration && (
                        <div className="mt-4 sm:mt-5">
                            <h5 className="font-semibold mb-2 sm:mb-3 text-[13px] sm:text-[15px] font-orbitron text-gray-900">Estimated Duration</h5>
                            <p className="text-[13px] sm:text-[15px] leading-relaxed text-gray-700">{service.estimated_duration}</p>
                        </div>
                      )}
                      </div>
                    </div>
                  );
                })
              }
              
              {/* Add the separator for optional services if we have both types */}
              {services.some(s => !s.isSelectable && !s.name?.includes('(Optional)')) && 
               services.some(s => s.isSelectable || s.name?.includes('(Optional)')) && (
                <div className="border-t border-gray-200/80 mt-4 pt-4 mb-3">
                  <div className="text-center px-3 py-2 bg-[#e8f5e9] rounded-md border border-[#c8e6c9] text-sm font-medium text-gray-700 shadow-sm">
                    <span className="inline-flex items-center">
                      Select Any Additional Services Below Before Accepting An Offer
                    </span>
                  </div>
                </div>
              )}
              
              {/* Then display all optional services */}
              {services
                .filter(service => service.isSelectable || service.name?.includes('(Optional)'))
                .map((service, index) => {
                  const serviceId = service.id || `service-${services.indexOf(service)}`;
                  const isSelected = service.is_selected === true || localServiceToggle[serviceId] === true;
                  
                  // Only hide optional services that aren't selected when the offer is expanded
                  if (!isSelected && !isExpanded) {
                    return null;
                  }
                  
                  return (
                    <div key={`optional-${serviceId}`} className={`pt-3 sm:pt-4 pb-3 sm:pb-4 rounded-lg border-2 transition-all duration-200 ${isSelected ? 'bg-gray-50 border-green-400 shadow-sm' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="px-3 sm:px-4">
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div className="flex-1">
                            <h4 className={`font-semibold text-[14px] sm:text-[16px] font-orbitron ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>
                            {service.name.replace(/ \(Optional\)/gi, '')}
                          </h4>
                        </div>
                        <div className="flex items-center">
                          {/* Show discount pricing if service has discount */}
                          {(service as any).discount_amount && (service as any).discount_amount > 0 ? (
                            <div className="text-right mr-3">
                              <div className={`text-sm line-through ${!isSelected ? 'text-gray-400' : 'text-gray-500'}`}>
                                {formatCurrency(typeof service.price === 'number' ? service.price : 0)}
                              </div>
                              <div className={`text-lg font-bold ${!isSelected ? 'text-gray-400' : 'text-[#00CC4D]'}`}>
                                {formatCurrency((typeof service.price === 'number' ? service.price : 0) - ((service as any).discount_amount || 0))}
                              </div>
                            </div>
                          ) : (
                            <div className={`text-lg font-bold mr-3 ${!isSelected ? 'text-gray-400' : 'text-[#00CC4D]'}`}>
                              {formatCurrency(typeof service.price === 'number' ? service.price : 0)}
                            </div>
                          )}
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log(`Toggle clicked for ${service.name}`);
                              if (supabase && serviceId) {
                                const newState = !isSelected;
                                console.log(`Updating service ${serviceId} to ${newState}`);
                                supabase
                                  .from('proposal_services')
                                  .update({ is_selected: newState })
                                  .eq('id', serviceId)
                                  .then(result => {
                                    console.log("Toggle result:", result);
                                    if (!result.error) {
                                      // Update the local state instead of reloading the page
                                      service.is_selected = newState;
                                      // Force re-render by updating a state variable
                                      setLocalServiceToggle(prev => ({
                                        ...prev,
                                        [serviceId]: newState
                                      }));
                                    } else {
                                      console.error("Error toggling service:", result.error);
                                    }
                                  });
                              }
                            }}
                            className={`
                              relative w-12 h-6 rounded-full p-1 flex transition-all duration-300 shadow-inner
                              ${isSelected ? 'bg-[#00CC4D] justify-end' : 'bg-white justify-start'}
                              border-2 ${isSelected ? 'border-[#00CC4D]' : 'border-[#00CC4D]/40'}
                              cursor-pointer hover:border-[#00CC4D] hover:shadow-md
                              outline outline-2 outline-offset-2 ${isSelected ? 'outline-[#00CC4D]/20' : 'outline-[#00CC4D]/10'}
                            `}
                            title={isSelected ? "Click to deselect" : "Click to select"}
                          >
                            <div className={`
                              w-4 h-4 rounded-full transition-all duration-300 flex items-center justify-center
                              ${isSelected ? 'bg-white text-[#00CC4D]' : 'bg-[#00CC4D]/40 text-white'}
                              hover:bg-[#00CC4D]
                            `}>
                              {isSelected && <span className="text-xs">✓</span>}
                            </div>
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        {service.description && (
                          <div className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed col-span-full mb-3 sm:mb-4">
                            <div className={`w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 ${!isSelected ? 'bg-gray-300' : 'bg-[#00CC4D]'}`} />
                              <div className={`${isSelected ? 'text-gray-700' : 'text-gray-500'}`}>
                              <RichTextDescription 
                                description={service.description} 
                                  className={`prose-sm ${isSelected ? 'text-gray-700 prose-headings:text-gray-800 prose-strong:text-gray-800 prose-em:text-gray-600' : 'text-gray-500 prose-headings:text-gray-600 prose-strong:text-gray-600 prose-em:text-gray-400'}`} 
                              />
                            </div>
                          </div>
                        )}
                        {service.details && service.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed">
                            <div className={`w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 ${!isSelected ? 'bg-gray-300' : 'bg-[#00CC4D]'}`} />
                              <p className={`${isSelected ? 'text-gray-700' : 'text-gray-500'}`}>{detail}</p>
                          </div>
                        ))}
                      </div>

                      {service.benefits && service.benefits.length > 0 && (
                        <div className="mt-4 sm:mt-5">
                            <h5 className={`font-semibold mb-2 sm:mb-3 text-[13px] sm:text-[15px] font-orbitron ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>Key Benefits</h5>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                            {service.benefits.map((benefit, benefitIndex) => (
                              <div key={benefitIndex} className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed">
                                <div className={`w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 ${!isSelected ? 'bg-gray-300' : 'bg-[#00CC4D]'}`} />
                                  <p className={`${isSelected ? 'text-gray-700' : 'text-gray-500'}`}>{benefit}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {service.estimated_duration && (
                        <div className="mt-4 sm:mt-5">
                            <h5 className={`font-semibold mb-2 sm:mb-3 text-[13px] sm:text-[15px] font-orbitron ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>Estimated Duration</h5>
                            <p className={`text-[13px] sm:text-[15px] leading-relaxed ${isSelected ? 'text-gray-700' : 'text-gray-500'}`}>{service.estimated_duration}</p>
                        </div>
                      )}
                      </div>
                    </div>
                  );
                })
              }

              {/* Products Section */}
              {products && products.length > 0 && (
                <>
                  {/* Add separator if we have services */}
                  {services.length > 0 && (
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200/80"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <div className="bg-gray-50 px-2">
                          <Package2 className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Display all products */}
                  {products.map((product, index) => {
                    const productId = product.id || `product-${index}`;
                    return (
                      <div key={`product-${productId}`} className="pt-3 sm:pt-4 pb-3 sm:pb-4 bg-white border-2 border-green-400 rounded-lg shadow-sm">
                        <div className="px-3 sm:px-4">
                          <div className="flex items-start justify-between mb-2 sm:mb-3">
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mr-3">
                                <ProductIcon product={product} />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-[14px] sm:text-[16px] font-orbitron text-gray-900">
                                  {product.name}
                                </h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[12px] sm:text-[13px] text-gray-600">
                                    Quantity: {product.quantity}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center">
                              {/* Show discount pricing if product has discount */}
                              {product.discount_amount && product.discount_amount > 0 ? (
                                <div className="text-right">
                                  <div className="text-sm text-gray-500 line-through">
                                    {formatCurrency(typeof product.price === 'number' ? product.price * product.quantity : 0)}
                                  </div>
                                  <div className="text-lg font-bold text-[#00CC4D]">
                                    {formatCurrency((typeof product.price === 'number' ? product.price * product.quantity : 0) - (product.discount_amount || 0))}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-lg font-bold text-[#00CC4D]">
                                  {formatCurrency(typeof product.price === 'number' ? product.price * product.quantity : 0)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              <div className="border-t border-gray-200/80 pt-4 sm:pt-5 mt-4">
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex justify-between text-[13px] sm:text-[15px] text-gray-600">
                    <span>Subtotal</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  {totalDiscountAmount > 0 && (
                    <div className="flex justify-between text-[13px] sm:text-[15px] text-green-600">
                      <span>Discount</span>
                      <span>-{formatCurrency(totalDiscountAmount)}</span>
                    </div>
                  )}
                  {promo > 0 && (
                    <div className="flex justify-between text-[13px] sm:text-[15px] text-blue-600">
                      <span>Promo</span>
                      <span>-{formatCurrency(promo)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-[13px] sm:text-[15px] text-gray-600">
                    <span>GST ({(tax * 100).toFixed(0)}%)</span>
                    <span>+{formatCurrency(taxAmount)}</span>
                  </div>
                  <div className="flex justify-between text-base sm:text-lg font-bold pt-3 mt-1 border-t border-gray-200/50">
                    <span className="text-gray-800 font-orbitron">Total</span>
                    <span className="text-gray-800 font-orbitron">{formatCurrency(total)}</span>
                  </div>
                </div>

                {/* Second Select Button at Bottom */}
                <div className="pt-4 sm:pt-5 mt-4 border-t border-gray-200/50">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect();
                    }}
                    disabled={isSelecting}
                    className={`
                      w-full relative px-4 sm:px-7 py-3 sm:py-4 rounded-full text-sm sm:text-base font-medium
                      transition-all duration-300 font-orbitron
                      before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-r
                      before:transition-all before:duration-300 before:opacity-90
                      ${isSelected 
                        ? 'before:from-[#00CC4D] before:to-[#00B344] text-white shadow-lg shadow-[#00CC4D]/30 scale-95 ring-1 sm:ring-2 ring-[#00CC4D]/50 ring-offset-1 sm:ring-offset-2 ring-offset-black/50'
                        : isSelecting
                          ? 'before:from-gray-500 before:to-gray-600 text-white cursor-not-allowed opacity-75'
                          : 'before:from-[#00CC4D] before:to-[#00B344] text-white hover:scale-105 hover:shadow-lg hover:shadow-[#00CC4D]/30'
                      }
                      overflow-hidden group
                      disabled:hover:scale-100 disabled:hover:shadow-none
                    `}
                  >
                    <span className="relative inline-flex items-center justify-center">
                      {isSelecting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          SELECTING...
                        </>
                      ) : isSelected ? 'ACCEPTED' : 'ACCEPT THIS OFFER'}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



// Add this utility function at the top level of the file or inside the component
const fetchSelectedServicesForProposal = async (supabaseClient: any, proposalId: string) => {
  if (!supabaseClient) return null;
  
  try {
    // First, find the approved offer ID
    const { data: proposalData, error: proposalError } = await supabaseClient
      .from('proposals')
      .select('notes')
      .eq('id', proposalId)
      .single();
      
    if (proposalError || !proposalData?.notes) {
      console.error("Error fetching proposal notes:", proposalError);
      return null;
    }
    
    // Parse the notes to get the approved offer ID
    let approvedOfferId: string | null = null;
    try {
      const notes = JSON.parse(proposalData.notes);
      approvedOfferId = notes.approved_offer_id;
    } catch (e) {
      console.error("Error parsing notes:", e);
    }
    
    if (!approvedOfferId) {
      console.error("No approved offer ID found in notes");
      return null;
    }
    
    // Fetch all services for this proposal and offer that are selected
    // Use proposal_services table directly to ensure we get discount information
    const { data: servicesData, error: servicesError } = await supabaseClient
      .from('proposal_services')
      .select(`
        id, name, description, price, quantity, is_selected, isSelectable, is_selectable,
        discount_amount, discount_type, discount_value,
        services!inner(name, description, price)
      `)
      .eq('proposal_id', proposalId)
      .eq('is_selected', true);
      
    if (servicesError) {
      console.error("Error fetching selected services:", servicesError);
      return null;
    }
    
    console.log(`Found ${servicesData.length} selected services for proposal ${proposalId}`);
    
    // Make sure all required properties are available for display
    const processedServices = servicesData.map((service: any) => {
      // Use proposal service data first, fallback to joined service data
      const serviceInfo = service.services || {};
      
      return {
        id: service.id,
        name: service.name || serviceInfo.name || 'Unknown Service',
        description: service.description || serviceInfo.description || '',
        price: typeof service.price === 'number' ? service.price : (typeof serviceInfo.price === 'number' ? serviceInfo.price : 0),
        quantity: typeof service.quantity === 'number' ? service.quantity : 1,
        is_selected: service.is_selected,
        isSelectable: service.isSelectable || service.is_selectable, // Map from either property
        // Ensure discount information is preserved
        discount_amount: typeof service.discount_amount === 'number' ? service.discount_amount : 0,
        discount_type: service.discount_type || null,
        discount_value: typeof service.discount_value === 'number' ? service.discount_value : null
      };
    });
    

    
    return { 
      services: processedServices,
      approvedOfferId
    };
  } catch (err) {
    console.error("Error in fetchSelectedServicesForProposal:", err);
    return null;
  }
};

// Add proper types for the ThankYouPage component props
interface ThankYouPageProps {
  proposal: Proposal;
  business: Business;
  selectedOffer: string | null;
  supabase: any;
}

// Add a new DeclinePage component
interface DeclinePageProps {
  business: Business;
}

const DeclinePage: React.FC<DeclinePageProps> = ({ business }) => {
  return (
    <div className="min-h-screen bg-black">
      <div className="relative max-w-4xl mx-auto px-3 sm:px-6 py-8 sm:py-12">
        <div className="text-center space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-500/20 text-red-400">
            <X className="w-8 h-8 sm:w-10 sm:h-10" />
          </div>
          
          <div className="space-y-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-white font-orbitron">Proposal Declined</h2>
            <p className="text-blue-200/70 text-sm sm:text-base">
              You have declined all offers in this proposal. If you'd like to discuss other options, please contact us directly.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 mt-8">
            <h3 className="text-sm sm:text-base font-medium text-blue-100 mb-3">Next Steps</h3>
            <p className="text-blue-200/70 text-sm leading-relaxed">
              We appreciate you taking the time to review our proposal. If you have any questions or would like to discuss alternative solutions, please don't hesitate to reach out.
            </p>
          </div>

          <div className="text-center mt-8 text-xs sm:text-sm text-blue-200/70">
            <p className="space-y-1 sm:space-y-0">
              <span className="block sm:inline">Contact {business?.name || 'the business'}</span>
              {business?.email && (
                <span className="block sm:inline sm:ml-1">
                  at <a href={`mailto:${business.email}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.email}</a>
                  {business?.phone && (
                    <> or call at <a href={`tel:${business.phone}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.phone}</a></>
                  )}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Update the ThankYouPage component with proper types
const ThankYouPage: React.FC<ThankYouPageProps> = ({ proposal, business, selectedOffer, supabase }) => {
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false); // Start as false since we pre-calculate
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // **INSTANT DATA PROCESSING** - Use existing proposal data instead of fetching
    const processSelectedData = () => {
      console.log("🚀 Processing selected services and products instantly from existing data");
      
      if (!proposal || !selectedOffer) {
        console.log("Missing proposal or selectedOffer");
        setSelectedServices([]);
        setSelectedProducts([]);
        return;
      }
        
      // Filter services for the selected offer
      const offerServices = proposal.services.filter(service => 
        service.offer?.id === selectedOffer
      );
      
      console.log(`Found ${offerServices.length} services for selected offer`);
      
      // Include: all required services + selected optional services
      const servicesToShow = offerServices.filter(service => {
        const isOptional = service.isSelectable === true;
        const isSelected = service.is_selected === true;
        
        // Include if: (not optional) OR (optional AND selected)
        const shouldInclude = !isOptional || (isOptional && isSelected);
        
        console.log(`Service ${service.name}: optional=${isOptional}, selected=${isSelected}, include=${shouldInclude}`);
        return shouldInclude;
      });
      
      // Filter products for the selected offer
      const offerProducts = proposal.products?.filter(product => 
        product.offer?.id === selectedOffer
      ) || [];
      
      console.log(`Found ${offerProducts.length} products for selected offer`);
      console.log(`✅ Showing ${servicesToShow.length} services and ${offerProducts.length} products instantly`);

      setSelectedServices(servicesToShow);
      setSelectedProducts(offerProducts);
      
      // Load product photos if we have products
      if (offerProducts.length > 0) {
        loadProductPhotos(offerProducts);
      }
    };
    
    const loadProductPhotos = async (products: Product[]) => {
      try {
        const stockItemIds = products
          .map(product => product.stock_item_id)
          .filter(Boolean) as string[];
          
        if (stockItemIds.length > 0) {
          const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
          const photoUrls: Record<string, string> = {};
          
          if (photoMap && typeof photoMap === 'object') {
            Object.entries(photoMap).forEach(([itemId, photo]) => {
              if (photo && photo.photo_url) {
                photoUrls[itemId] = photo.photo_url;
              }
            });
          }
          
          setProductPhotos(photoUrls);
          console.log('✅ Product photos loaded for thank you page:', Object.keys(photoUrls).length);
        }
      } catch (photoError) {
        console.error('Error loading product photos for thank you page:', photoError);
        setProductPhotos({});
      }
    };
        
    processSelectedData();
  }, [proposal, selectedOffer]);

  // Component for product icon in thank you page - shows photo if available, otherwise green Package2 icon
  const ProductIcon = ({ product }: { product: Product }) => {
    const photoUrl = productPhotos[product.stock_item_id || ''];
    
    if (photoUrl) {
      return (
        <div className="h-8 w-8 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
          <img 
            src={photoUrl} 
            alt={product.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, show fallback icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = '<div class="h-4 w-4 text-green-400"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29,7 12,12 20.71,7"/><line x1="12" x2="12" y1="22" y2="12"/></svg></div>';
                parent.className = "h-8 w-8 rounded-lg bg-green-100/20 flex items-center justify-center";
              }
            }}
          />
        </div>
      );
    }
    
    return (
      <div className="h-8 w-8 rounded-lg bg-green-100/20 flex items-center justify-center">
        <Package2 className="h-4 w-4 text-green-400" />
      </div>
    );
  };

  // Calculate total amount safely with proper currency rounding
  const serviceSubtotal = Math.round(selectedServices.reduce(
    (sum, service) => sum + (typeof service.price === 'number' ? service.price : 0), 
    0
  ) * 100) / 100;
  
  const productSubtotal = Math.round(selectedProducts.reduce(
    (sum, product) => sum + (typeof product.price === 'number' ? product.price * product.quantity : 0), 
    0
  ) * 100) / 100;
  
  const subtotal = serviceSubtotal + productSubtotal;
  
  // Calculate total discount amount
  const serviceDiscountAmount = Math.round(selectedServices.reduce(
    (sum, service) => sum + ((service as any).discount_amount || 0),
    0
  ) * 100) / 100;
  
  const productDiscountAmount = Math.round(selectedProducts.reduce(
    (sum, product) => sum + (product.discount_amount || 0),
    0
  ) * 100) / 100;
  
  const totalDiscountAmount = serviceDiscountAmount + productDiscountAmount;
  
  // Calculate discounted subtotal
  const discountedSubtotal = Math.round((subtotal - totalDiscountAmount) * 100) / 100;
  
  // Calculate tax (GST) on discounted amount - default to 5% if not specified
  const tax = 0.05; // 5% GST
  const taxAmount = Math.round((discountedSubtotal * tax) * 100) / 100;
  const totalAmount = Math.round((discountedSubtotal + taxAmount) * 100) / 100;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-blue-100 mb-2 font-orbitron">Error</h2>
          <p className="text-blue-200/70">{error}</p>
        </div>
      </div>
    );
  }

  if (!proposal || !business) return null;

  // Show the decline page if status is declined
  if (proposal.status === 'declined') {
    return <DeclinePage business={business} />;
  }

  // Show thank you page if approved
  if (proposal.status === 'approved') {
    return (
      <div className="min-h-screen bg-black">
        <div className="relative max-w-4xl mx-auto px-3 sm:px-6 py-8 sm:py-12">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#00CC4D]/20 text-[#00CC4D]">
              <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl sm:text-3xl font-bold text-white font-orbitron">Thank You!</h2>
              <p className="text-blue-200/70 text-sm sm:text-base">
                We've received your selection and will reach out to you shortly to proceed with the next steps.
              </p>
              {error && (
                <p className="text-xs text-yellow-300 mt-1">Error: {error}</p>
              )}
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 mt-8">
              <h3 className="text-sm sm:text-base font-medium text-blue-100 mb-3">
                {selectedProducts.length > 0 ? 'Selected Services & Products' : 'Selected Services'}
              </h3>
              
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
                </div>
              ) : (selectedServices.length > 0 || selectedProducts.length > 0) ? (
                <>
                  {selectedServices.map((service, index) => (
                    <div key={index} className="text-left mb-4 last:mb-0 pb-4 last:pb-0 border-b border-white/10 last:border-b-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">{service.name.replace(/ \(Optional\)/gi, '')}</p>
                          </div>
                                        {service.description && (
                <div className="mt-1">
                  <RichTextDescription 
                    description={service.description} 
                    className="prose-sm text-blue-200/70 prose-headings:text-blue-200 prose-strong:text-blue-100 prose-em:text-blue-300/70" 
                  />
                </div>
              )}
                        </div>
                        <div className="text-right">
                          {/* Show discount pricing if service has discount */}
                          {(service as any).discount_amount && (service as any).discount_amount > 0 ? (
                            <div>
                              <p className="text-sm text-blue-300/60 line-through">
                                ${typeof service.price === 'number' ? service.price.toFixed(2) : '0.00'}
                              </p>
                              <p className="text-lg font-bold text-[#00CC4D]">
                                ${((typeof service.price === 'number' ? service.price : 0) - ((service as any).discount_amount || 0)).toFixed(2)}
                              </p>
                            </div>
                          ) : (
                            <p className="text-lg font-bold text-[#00CC4D]">
                              ${typeof service.price === 'number' ? service.price.toFixed(2) : '0.00'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Products Section */}
                  {selectedProducts.length > 0 && (
                    <>
                      {selectedServices.length > 0 && (
                        <div className="my-4 border-t border-white/10 pt-4">
                          <h4 className="text-sm font-medium text-blue-100 mb-3">Products</h4>
                        </div>
                      )}
                      {selectedProducts.map((product, index) => (
                        <div key={index} className="text-left mb-4 last:mb-0 pb-4 last:pb-0 border-b border-white/10 last:border-b-0">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mr-3">
                                <ProductIcon product={product} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-white font-medium">{product.name}</p>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-blue-200/70">
                                    Quantity: {product.quantity}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              {/* Show discount pricing if product has discount */}
                              {product.discount_amount && product.discount_amount > 0 ? (
                                <div>
                                  <p className="text-sm text-blue-300/60 line-through">
                                    ${(typeof product.price === 'number' ? product.price * product.quantity : 0).toFixed(2)}
                                  </p>
                                  <p className="text-lg font-bold text-[#00CC4D]">
                                    ${((typeof product.price === 'number' ? product.price * product.quantity : 0) - (product.discount_amount || 0)).toFixed(2)}
                                  </p>
                                </div>
                              ) : (
                                <p className="text-lg font-bold text-[#00CC4D]">
                                  ${(typeof product.price === 'number' ? product.price * product.quantity : 0).toFixed(2)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className="text-blue-200/70">Subtotal:</span>
                        <span className="text-[#00CC4D]">
                          ${subtotal.toFixed(2)}
                        </span>
                      </div>
                      {totalDiscountAmount > 0 && (
                        <div className="flex justify-between text-sm sm:text-base">
                          <span className="text-blue-200/70">Discount:</span>
                          <span className="text-[#00CC4D]">
                            -${totalDiscountAmount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className="text-blue-200/70">GST ({(tax * 100).toFixed(0)}%):</span>
                        <span className="text-[#00CC4D]">
                          ${taxAmount.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm sm:text-base pt-2 border-t border-white/10">
                        <span className="text-blue-200/70 font-medium">Total:</span>
                        <span className="text-lg font-bold text-[#00CC4D]">
                          ${totalAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-right text-blue-200/70 mt-1">inc. GST</p>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-blue-200/70">
                    {error || 'No services or products selected'}
                  </p>
                </div>
              )}
            </div>

            <div className="text-center mt-8 text-xs sm:text-sm text-blue-200/70">
              <p className="space-y-1 sm:space-y-0">
                <span className="block sm:inline">If you have any questions, contact {business?.name || 'the business'}</span>
                {business?.email && (
                  <span className="block sm:inline sm:ml-1">
                    at <a href={`mailto:${business.email}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.email}</a>
                    {business?.phone && (
                      <> or call at <a href={`tel:${business.phone}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.phone}</a></>
                    )}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Fix the missing type for the isSelectedServiceToggle state
interface ServiceToggleState {
  [id: string]: boolean;
}

// Update the PublicProposal component
const PublicProposal: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [expandedOffers, setExpandedOffers] = useState<string[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [selectedOptionalServices, setSelectedOptionalServices] = useState<Set<string>>(new Set());
  const [directlyFetchedServices, setDirectlyFetchedServices] = useState<any[]>([]);
  const [isSelectedServiceToggle, setIsSelectedServiceToggle] = useState<ServiceToggleState>({});
  const [selectingOffer, setSelectingOffer] = useState<string | null>(null); // For button loading state
  const [showDeclineModal, setShowDeclineModal] = useState<boolean>(false);
  const [declineNote, setDeclineNote] = useState<string>('');
  const [isDeclining, setIsDeclining] = useState<boolean>(false);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});

  // Helper function to directly update a service selection state
  const updateServiceSelection = async (serviceId: string, isSelected: boolean) => {
    if (!supabase) return false;
    
    try {
      const { error } = await supabase
        .from('proposal_services')
        .update({ is_selected: isSelected })
        .eq('id', serviceId);
        
      return !error;
    } catch (err) {
      console.error(`Error updating service ${serviceId}:`, err);
      return false;
    }
  };

  // Update the handleToggleSelectableService function with proper types
  const handleToggleSelectableService = (serviceId: string) => {
    setIsSelectedServiceToggle(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
    
    // Update the services array with the new toggle state
    setProposal(prevProposal => {
      if (!prevProposal) return prevProposal;
      
      const updatedServices = prevProposal.services.map(service => {
        if (service.id === serviceId) {
          return {
            ...service,
            is_selected: !isSelectedServiceToggle[serviceId]
          };
        }
        return service;
      });
      
      return {
        ...prevProposal,
        services: updatedServices
      };
    });
  };

  // Fix any remaining typing issues in the renderService function
  const renderService = (service: Service) => {
    // ... existing code ...
  };
  
  // Update the handleAcceptOffer function with proper types
  const handleAcceptOffer = async (offerId: string) => {
    // ... existing code ...
  };

  // Add a function to ensure optional services are properly initialized
  const resetOptionalServiceSelections = async (services: Service[]) => {
    if (!supabase) return;
    
    // For each optional service, ensure it's initially not selected in the database
    for (const service of services) {
      if (!service.isSelectable || !service.id || service.is_selected === false) continue;
      
      // If it's an optional service and not explicitly set to false in the database,
      // make sure it's set to false
      console.log(`Resetting optional service ${service.id} to unselected state`);
      
      try {
        const { error } = await supabase.rpc('update_service_selection', {
          p_service_id: service.id,
          p_is_selected: false
        });
        
        if (error) {
          console.error(`Error resetting service ${service.id}:`, error);
        }
      } catch (err) {
        console.error(`Error calling service reset:`, err);
      }
    }
  };

  const fetchProposalData = async () => {
    try {
      console.log('🚀 Starting FAST proposal data fetch with token:', token);
      
      if (!token) {
        console.error('No token provided in URL');
        setError('No proposal token provided');
        setIsLoading(false);
        return;
      }

      if (!supabase) {
        console.error('Supabase client is not initialized');
        setError('Database connection is not available');
        setIsLoading(false);
        return;
      }

      // **SINGLE OPTIMIZED RPC CALL** - Replaces 4+ sequential queries
      console.log('📡 Fetching ALL data in single optimized call...');
      const startTime = Date.now();
      
      const { data: result, error: rpcError } = await supabase
        .rpc('get_proposal_data_fast', { p_token: token });

      const loadTime = Date.now() - startTime;
      console.log(`⚡ Database call completed in ${loadTime}ms`);

      if (rpcError) {
        console.error('RPC Error:', rpcError);
        setError('Failed to load proposal data');
        setIsLoading(false);
        return;
      }

      if (!result) {
        console.error('No result from RPC');
        setError('No data returned from server');
        setIsLoading(false);
        return;
      }

      // Handle different error cases
      if (!result.success) {
        switch (result.error) {
          case 'INVALID_TOKEN':
            setError('Invalid proposal token - link not found');
            break;
          case 'EXPIRED':
        setError('Proposal link has expired');
        setExpired(true);
            break;
          case 'DATABASE_ERROR':
            setError(`Database error: ${result.message}`);
            break;
          default:
            setError(result.message || 'Unknown error occurred');
        }
        setIsLoading(false);
        return;
      }
      
      // **INSTANT DATA PROCESSING** - Data is already optimized from database
      const proposalData = result.proposal;
      const businessData = result.business;

      console.log('✅ Proposal data loaded instantly:', proposalData);
      console.log('✅ Services loaded:', proposalData.services?.length || 0);

      // If the proposal is approved, find and set the selected offer
      if (proposalData.status === 'approved') {
        const approvedService = proposalData.services?.find((service: any) => service.offer?.status === 'approved');
        if (approvedService?.offer?.id) {
          setSelectedOffer(approvedService.offer.id);
          console.log(`Found approved offer: ${approvedService.offer.id}`);
        }
      }

      // Fetch discount information for all services
      console.log('🔍 Fetching discount information for services...');
      const serviceIds = proposalData.services?.map((service: any) => service.id).filter(Boolean) || [];
      
      if (serviceIds.length > 0) {
        const { data: discountData, error: discountError } = await supabase
          .from('proposal_services')
          .select('id, discount_amount, discount_type, discount_value')
          .in('id', serviceIds);
          
        if (!discountError && discountData) {
          // Merge discount data with existing services
          const servicesWithDiscounts = proposalData.services?.map((service: any) => {
            const discountInfo = discountData.find(d => d.id === service.id);
            if (discountInfo) {
              return {
                ...service,
                discount_amount: discountInfo.discount_amount || 0,
                discount_type: discountInfo.discount_type,
                discount_value: discountInfo.discount_value
              };
            }
            return service;
          });
          
          proposalData.services = servicesWithDiscounts;
          console.log('✅ Discount data merged with services');
        } else {
          console.warn('Failed to fetch discount data:', discountError);
        }
      }

      // Fetch products for this proposal
      console.log('🔍 Fetching products for proposal...');
      const { data: productsData, error: productsError } = await supabase
        .from('proposal_products_with_details')
        .select('*')
        .eq('proposal_id', proposalData.id);
        
      if (!productsError && productsData) {
        // Group products by offer
        const productsByOffer: Record<string, any[]> = {};
        productsData.forEach((product: any) => {
          if (!productsByOffer[product.offer_id]) {
            productsByOffer[product.offer_id] = [];
          }
          productsByOffer[product.offer_id].push({
            id: product.id,
            name: product.name,
            price: product.price,
            quantity: product.quantity,
            category_name: product.stock_category_name || product.category_name,
            stock_item_id: product.stock_item_id,
            discount_amount: product.discount_amount || 0,
            discount_type: product.discount_type,
            discount_value: product.discount_value,
            offer: {
              id: product.offer_id,
              title: '', // Will be filled from services
              status: ''
            }
          });
        });
        
        // Add products to proposal data
        proposalData.products = Object.values(productsByOffer).flat();
        console.log('✅ Products loaded:', proposalData.products?.length || 0);
        
        // Load product photos
        const stockItemIds = proposalData.products
          .map((product: any) => product.stock_item_id)
          .filter(Boolean);
          
        if (stockItemIds.length > 0) {
          try {
            const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
            const photoUrls: Record<string, string> = {};
            
            if (photoMap && typeof photoMap === 'object') {
              Object.entries(photoMap).forEach(([itemId, photo]) => {
                if (photo && photo.photo_url) {
                  photoUrls[itemId] = photo.photo_url;
                }
              });
            }
            
            setProductPhotos(photoUrls);
            console.log('✅ Product photos loaded:', Object.keys(photoUrls).length);
          } catch (photoError) {
            console.error('Error loading product photos:', photoError);
            setProductPhotos({});
          }
        }
      } else {
        console.warn('Failed to fetch products data:', productsError);
        proposalData.products = [];
      }

      // Set all data at once
      setProposal(proposalData);
      setBusiness(businessData);

      // Find all unique offer IDs (but don't expand them by default)
      const offerIds = proposalData.services?.reduce((ids: string[], service: any) => {
        if (service.offer?.id && !ids.includes(service.offer.id)) {
          ids.push(service.offer.id);
        }
        return ids;
      }, [] as string[]) || [];
      
      // Keep offers collapsed by default - don't auto-expand
      setExpandedOffers([]);
      setIsLoading(false);

      console.log(`🎉 TOTAL LOAD TIME: ${Date.now() - startTime}ms (target: <1000ms)`);
      
    } catch (err) {
      console.error('Error in fetchProposalData:', err);
      setError(`An unexpected error occurred: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsLoading(false);
    }
  };

  const handleToggleOffer = (offerId: string) => {
    setExpandedOffers(prev =>
      prev.includes(offerId)
        ? prev.filter(id => id !== offerId)
        : [...prev, offerId]
    );
  };

  const handleSelectOffer = async (offerId: string) => {
    if (!proposal || !supabase) {
      console.error("Cannot select offer: supabase client or proposal is null");
      return;
    }

    try {
      // Show loading state on button
      setSelectingOffer(offerId);
      
      // Get all services for this offer
      const offerServices = proposal.services.filter(service => 
        service.offer?.id === offerId
      );
      
      console.log(`🚀 Fast approval: offer ${offerId} with ${offerServices.length} services`);
      
      // Store original proposal state for potential rollback
      const originalProposal = proposal;
      
      // **OPTIMISTIC UI UPDATE** - Update state immediately for instant response
      const optimisticProposal = {
        ...proposal,
        status: 'approved' as const,
        notes: JSON.stringify({
          approved_offer_id: offerId,
          approved_at: new Date().toISOString(),
        }),
        services: proposal.services.map(service => {
          if (!service.offer) return service;
          
          return {
            ...service,
            offer: {
              ...service.offer,
              status: service.offer.id === offerId ? 'approved' : 'declined'
            }
          };
        })
      };
      
      // Apply optimistic update after brief delay for smooth UX
      setTimeout(() => {
        setSelectedOffer(offerId);
        setSelectingOffer(null);
        setProposal(optimisticProposal);
      }, 300);
      
      // **DATABASE OPERATION** - Perform actual approval with fallback
      try {
        console.log('🔄 Attempting to approve proposal offer...');
        
        // Try the fast RPC first, fallback to regular function
        let approvalSuccess = false;
        
        try {
          console.log('🚀 Trying fast RPC function...');
          const { error: fastError } = await supabase.rpc('approve_proposal_offer_fast', {
            p_proposal_id: proposal.id,
            p_offer_id: offerId,
            p_business_id: proposal.business_id,
            p_customer_id: proposal.customer_id,
            p_proposal_title: proposal.title,
            p_offer_title: offerServices[0]?.offer?.title || 'Selected Offer'
          });
          
          if (!fastError) {
            console.log('✅ Fast RPC succeeded');
            approvalSuccess = true;
          } else {
            console.warn('⚠️ Fast RPC failed, trying fallback:', fastError);
            throw new Error('Fast RPC failed');
          }
        } catch (fastRpcError) {
          console.log('🔄 Fast RPC failed, using fallback method...');
          
          // Fallback to the regular RPC function
          try {
            const { error: fallbackError } = await supabase.rpc('approve_proposal_offer', { 
              p_offer_id: offerId 
            });
            
            if (!fallbackError) {
              console.log('✅ Fallback RPC succeeded');
              approvalSuccess = true;
            } else {
              console.error('❌ Fallback RPC also failed:', fallbackError);
              throw fallbackError;
            }
          } catch (fallbackRpcError) {
            console.error('❌ Both RPC methods failed:', fallbackRpcError);
            throw fallbackRpcError;
          }
        }
        
        if (approvalSuccess) {
          console.log('✅ Database update completed successfully');

          
          // Keep the optimistic update - don't revert since approval succeeded
          // The UI should now show the thank you page
          
          // Skip background verification to preserve discount data
          // The optimistic update already has all necessary data including discounts
          console.log('🎯 Skipping background verification to preserve discount data in approved proposal');
        }
        
      } catch (rpcError) {
        console.error('❌ All approval methods failed:', rpcError);
        
        // Revert optimistic update only if the database operation completely failed
        setProposal(originalProposal);
        setSelectedOffer(null);
        setSelectingOffer(null);
        
        // Show user-friendly error
        alert('Failed to approve proposal. Please try again or contact support if the issue persists.');
      }
      
    } catch (err) {
      console.error('Error in offer selection:', err);
      setSelectingOffer(null);
      setSelectedOffer(null);
      alert(`Failed to select offer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Open decline confirmation modal
  const handleDeclineAll = () => {
    setShowDeclineModal(true);
  };

  // Actually execute the decline with optional note
  const executeDeclineAll = async () => {
    if (!proposal || !supabase) return;

    try {
      setIsDeclining(true);
      console.log(`Declining proposal ${proposal.id} with note: "${declineNote}"...`);
      
      const declineData = {
        declined_at: new Date().toISOString(),
        declined_by: 'customer',
        ...(declineNote.trim() && { decline_reason: declineNote.trim() })
      };

      const { error } = await supabase
        .from('proposals')
        .update({ 
          status: 'declined',
          notes: JSON.stringify(declineData)
        })
        .eq('id', proposal.id);

      if (error) {
        console.error('Error declining proposal in database:', error);
        throw error;
      }

      console.log('✅ Proposal successfully declined in database');
      
      // Create proposal declined milestone
      try {
        const milestoneData = {
          type: 'custom' as const,
          title: 'Proposal Declined',
          description: `Proposal "${proposal.title}" was declined by customer${declineNote.trim() ? ` - Reason: ${declineNote.trim()}` : ''}`,
          timestamp: new Date().toISOString(),
          metadata: {
            proposal_title: proposal.title,
            entity_type: 'proposal',
            declined_at: new Date().toISOString(),
            declined_by: 'customer',
            ...(declineNote.trim() && { decline_reason: declineNote.trim() })
          }
        };

        await supabase
          .from('booking_milestones')
          .insert([{
            business_id: proposal.business_id,
            customer_id: proposal.customer_id,
            booking_id: proposal.id, // Using proposal ID as booking ID for milestones
            milestone: milestoneData
          }]);

        console.log('✅ Proposal declined milestone created successfully');
      } catch (milestoneError) {
        console.error('❌ Error creating proposal declined milestone:', milestoneError);
        // Continue even if milestone creation fails
      }
      
      // Update local state to trigger DeclinePage render
      setProposal({ 
        ...proposal, 
        status: 'declined',
        notes: JSON.stringify(declineData)
      });
      
      // Close modal
      setShowDeclineModal(false);
      setDeclineNote('');
      
      console.log('✅ Local proposal state updated to declined');
      
    } catch (err) {
      console.error('❌ Error declining proposal:', err);
      alert(`Failed to decline proposal: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsDeclining(false);
    }
  };

  // Prevent unnecessary data refetching after approval
  // The optimistic update should preserve discount data
  useEffect(() => {
    // Only fetch if proposal status changed to approved from another source (not our optimistic update)
    // We can skip this fetch since the ThankYouPage should use the existing proposal data
    if (proposal?.status === 'approved' && supabase) {
      console.log('Approved proposal detected - using existing data to preserve discounts');
      // Don't refetch data here as it may lose discount information
      // The proposal object already has all the data we need
    }
  }, [proposal?.id, proposal?.status, supabase]);

  useEffect(() => {
    fetchProposalData();
  }, [token]);

  // Function to handle toggle of optional service
  const handleToggleOptionalService = async (serviceId: string) => {
    if (!proposal || !supabase) {
      console.error("Cannot toggle service: supabase client or proposal is null");
      return;
    }
    
    try {
      // Find the service in our local state
      const service = proposal.services.find((s: any) => s.id === serviceId);
      if (!service) {
        console.error(`Service ${serviceId} not found in proposal`);
        return;
      }
      
      // Get the current selection state and toggle it
      const currentlySelected = service.is_selected === true;
      const newSelectionState = !currentlySelected;
      
      console.log(`Toggling service ${serviceId} (${service.name}) from ${currentlySelected} to ${newSelectionState}`);
      
      // Update local state immediately for responsive UI
      setProposal({
        ...proposal,
        services: proposal.services.map((s: any) => 
          s.id === serviceId 
            ? { ...s, is_selected: newSelectionState } 
            : s
        )
      });
      
      // Update the database
      const { error } = await supabase
        .from('proposal_services')
        .update({ is_selected: newSelectionState })
        .eq('id', serviceId);
        
      if (error) {
        console.error(`Error updating service ${serviceId}:`, error);
        
        // Revert local state if there was an error
        setProposal({
          ...proposal,
          services: proposal.services.map((s: any) => 
            s.id === serviceId 
              ? { ...s, is_selected: currentlySelected } 
              : s
          )
        });
        
        alert(`Failed to toggle service: ${error.message}`);
      } else {
        console.log(`Service ${serviceId} successfully updated in database`);
        
        // Verify the update with a direct query
        const { data: verifyDataArray, error: verifyError } = await supabase
          .from('proposal_services')
          .select('is_selected')
          .eq('id', serviceId)
          .limit(1);
        
        const verifyData = verifyDataArray?.[0];
          
        if (verifyError) {
          console.error(`Error verifying service update for ${serviceId}:`, verifyError);
        } else if (verifyData) {
          console.log(`Verified service ${serviceId} is now ${verifyData.is_selected ? 'selected' : 'unselected'}`);
          if (verifyData.is_selected !== newSelectionState) {
            console.warn(`Verification failed: expected ${newSelectionState} but got ${verifyData.is_selected}`);
          }
        }
      }
    } catch (err) {
      console.error('Error toggling service selection:', err);
      alert(`An error occurred: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-blue-100 mb-2 font-orbitron">Error</h2>
          <p className="text-blue-200/70">{error}</p>
        </div>
      </div>
    );
  }

  if (!proposal || !business) return null;

  // Show the decline page if status is declined
  if (proposal.status === 'declined') {
    return <DeclinePage business={business} />;
  }

  // Show thank you page if approved
  if (proposal.status === 'approved') {
    return (
      <ThankYouPage 
        proposal={proposal} 
        business={business} 
        selectedOffer={selectedOffer} 
        supabase={supabase}
      />
    );
  }

  // In the return statement, change how offers are grouped and mapped
  const renderOffers = () => {
    if (!proposal) return null;

    // Group services by offer ID
    const offerGroups: Record<string, Service[]> = {};
    
    proposal.services?.forEach(service => {
      if (service.offer?.id) {
        if (!offerGroups[service.offer.id]) {
          offerGroups[service.offer.id] = [];
        }
        offerGroups[service.offer.id].push(service);
      }
    });

    // Group products by offer ID
    const productGroups: Record<string, Product[]> = {};
    
    proposal.products?.forEach(product => {
      if (product.offer?.id) {
        if (!productGroups[product.offer.id]) {
          productGroups[product.offer.id] = [];
        }
        productGroups[product.offer.id].push(product);
      }
    });

    // Convert grouping to array for rendering
    const groupedOffers = Object.entries(offerGroups).map(([offerId, services]) => ({
      id: offerId,
      title: services[0]?.offer?.title || "Offer",
      services: services,
      products: productGroups[offerId] || []
    }));

    return (
      <div className="space-y-3 sm:space-y-4">
        {groupedOffers.map(offer => (
          <Offer
            key={offer.id}
            title={offer.title}
            services={offer.services}
            products={offer.products}
            productPhotos={productPhotos}
            isExpanded={expandedOffers.includes(offer.id)}
            isSelected={selectedOffer === offer.id}
            isSelecting={selectingOffer === offer.id}
            onToggle={() => handleToggleOffer(offer.id)}
            onSelect={() => handleSelectOffer(offer.id)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen text-blue-100 relative overflow-hidden" style={{
      background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.9) 70%, rgba(0,0,0,1) 100%)'
    }}>
      {/* Subtle animated overlay */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-green-500/5" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
      </div>

      {/* Business Header */}
      <div className="relative">
        <div className="bg-black/40 backdrop-blur-xl border-b border-white/10">
          <div className="max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
            <div className="flex flex-col items-center text-center mb-3 sm:mb-6">
              {business?.logo_url && (
                <img
                  src={business.logo_url}
                  alt={business.name}
                  className="h-12 sm:h-16 w-12 sm:w-16 object-contain rounded-lg sm:rounded-xl mb-3 sm:mb-4 bg-black/50 p-1.5 sm:p-2 border border-white/10"
                />
              )}
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-white font-orbitron">{business?.name || 'Business Name'}</h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        <div className="mb-4 sm:mb-8">
          <p className="text-center text-blue-100 bg-white/5 backdrop-blur-xl py-2.5 sm:py-4 px-3 sm:px-6 rounded-xl sm:rounded-2xl border border-white/10 text-xs sm:text-base">
            Select 1 of the {Object.keys(proposal.services?.reduce((acc: Record<string, boolean>, service: any) => {
              if (service.offer?.id) acc[service.offer.id] = true;
              return acc;
            }, {} as Record<string, boolean>) || {}).length} offers you would like to move forward with below
          </p>
          
        </div>

        {renderOffers()}

        {(proposal.status === 'sent' || proposal.status === 'viewed') && (
          <div className="text-center mt-6 sm:mt-12">
            <p className="text-xs sm:text-sm text-blue-200/70 mb-3 sm:mb-6 bg-white/5 backdrop-blur-xl py-2.5 sm:py-4 px-3 sm:px-6 rounded-xl sm:rounded-2xl border border-white/10">
              If you choose not to select any of the offers above, please click 'Decline All' below.
            </p>
            <button
              onClick={handleDeclineAll}
              className="
                inline-flex items-center px-4 sm:px-6 py-1.5 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium
                bg-red-500/20 text-red-400 border border-red-500/30
                hover:bg-red-500/30 transition-all duration-300
                font-orbitron
              "
            >
              <X className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
              DECLINE ALL
            </button>
          </div>
        )}

        <div className="text-center mt-6 sm:mt-12 text-xs sm:text-sm text-blue-200/70 bg-white/5 backdrop-blur-xl py-2.5 sm:py-4 px-3 sm:px-6 rounded-xl sm:rounded-2xl border border-white/10">
          <p className="space-y-1 sm:space-y-0">
            <span className="block sm:inline">If you have any questions, contact {business.name}</span>
            <span className="block sm:inline sm:ml-1">
              at <a href={`mailto:${business.email}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.email}</a>
              {' '}or call at{' '}
              <a href={`tel:${business.phone}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.phone}</a>
            </span>
          </p>
        </div>
      </div>

      {/* Decline Confirmation Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowDeclineModal(false)}
          />
          
          {/* Modal */}
          <div className="relative bg-black/90 backdrop-blur-xl border border-white/20 rounded-2xl p-6 sm:p-8 mx-4 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-500/20 mb-4">
                <X className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white font-orbitron mb-2">
                Decline All Offers
              </h3>
              <p className="text-sm text-blue-200/70">
                Are you sure you want to decline all offers? This action cannot be undone.
              </p>
            </div>

            {/* Optional Note Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-blue-200 mb-2">
                Reason for declining (optional)
              </label>
              <textarea
                value={declineNote}
                onChange={(e) => setDeclineNote(e.target.value)}
                placeholder="Let us know why you're declining..."
                className="
                  w-full px-3 py-3 rounded-xl
                  bg-white/5 border border-white/20
                  text-white placeholder-blue-300/50
                  focus:border-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-400/20
                  resize-none
                "
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-blue-300/50 mt-1">
                {declineNote.length}/500 characters
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeclineModal(false)}
                className="
                  flex-1 px-4 py-2.5 rounded-xl text-sm font-medium
                  bg-white/10 text-blue-200 border border-white/20
                  hover:bg-white/20 transition-all duration-300
                  font-orbitron
                "
              >
                Cancel
              </button>
              <button
                onClick={executeDeclineAll}
                disabled={isDeclining}
                className="
                  flex-1 px-4 py-2.5 rounded-xl text-sm font-medium
                  bg-red-500/20 text-red-400 border border-red-500/30
                  hover:bg-red-500/30 transition-all duration-300
                  disabled:opacity-50 disabled:cursor-not-allowed
                  font-orbitron flex items-center justify-center
                "
              >
                {isDeclining ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-400 border-t-transparent mr-2" />
                    Declining...
                  </>
                ) : (
                  'DECLINE ALL'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PublicProposal;


